import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create Service
export const createService = createAsyncThunk(
  'services/createService',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}dashboard/create-service`,  
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get All Services
export const fetchServices = createAsyncThunk(
  'services/fetchServices',
  async ({ page, limit, search = '', deleted = '', homepage = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}dashboard/get-all-services-dashboard?page=${page}&limit=${limit}&search=${search}&deleted=${deleted}&homepage=${homepage}`
      );
      return {
        items: response.data.items,
        total: response.data.totalItems,
        currentPage: response.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.totalItems / limit),
        itemCount: response.data.items.length
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete Service
export const deleteService = createAsyncThunk(
  'services/deleteService',
  async (serviceId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}dashboard/delete-service/${serviceId}`
      );
      return serviceId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Service
export const updateService = createAsyncThunk(
  'services/updateService',
  async ({ serviceId, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/update-service/${serviceId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Set Homepage Order
export const setHomepageOrder = createAsyncThunk(
  'services/setHomepageOrder',
  async ({ serviceId, homepageOrder }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/set-homepage-order/${serviceId}`,
        { homepageOrder },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return {
        serviceId,
        homepageOrder,
        message: response.data.message,
        replacedService: response.data.replacedService || null
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to update position',
        status: error.response?.status
      });
    }
  }
);

const serviceSlice = createSlice({
  name: 'services',
  initialState: {
    services: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
    success: false
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Service
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.services.unshift({ ...action.payload, status: 'active' });
        state.total += 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Services
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Service
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.services = state.services.map(service =>
          service.id === action.payload
            ? { ...service, status: 'deleted', homepage_order: 0 }
            : service
        );
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Service
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.services.findIndex(p => p.id === action.payload.serviceId);
        if (index !== -1) {
          state.services[index] = { ...state.services[index], ...action.payload, status: 'active' };
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Set Homepage Order
      .addCase(setHomepageOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(setHomepageOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const { serviceId, homepageOrder } = action.payload;
        state.services = state.services.map(service => 
          service.id === serviceId 
            ? { ...service, homepage_order: homepageOrder } 
            : service
        );
        if (action.payload.replacedService) {
          state.services = state.services.map(service =>
            service.id === action.payload.replacedService.id
              ? { ...service, homepage_order: 0 }
              : service
          );
        }
      })
      .addCase(setHomepageOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess } = serviceSlice.actions;
export default serviceSlice.reducer;